import axios from 'axios';

const API_BASE_URL = '/api';

interface TranslateResponse {
  success: boolean;
  message: string;
  data?: {
    text: string;
    translation: string;
    from: string;
    to: string;
  };
}

interface BatchTranslateResponse {
  success: boolean;
  message: string;
  data?: Array<{
    text: string;
    translation: string;
    from: string;
    to: string;
  }>;
}

const translationCache: Record<string, string> = {};
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;

interface CacheEntry {
  translation: string;
  timestamp: number;
}

const cache: Record<string, CacheEntry> = {};

export const translate = async (
  text: string,
  from: string = 'zh',
  to: string = 'en'
): Promise<string> => {
  if (!text.trim()) {
    return text;
  }

  const cacheKey = `${text}:${from}:${to}`;
  
  if (cache[cacheKey] && Date.now() - cache[cacheKey].timestamp < CACHE_EXPIRY_MS) {
    return cache[cacheKey].translation;
  }

  try {
    const response = await axios.post<TranslateResponse>(
      `${API_BASE_URL}/translate`,
      { text, from, to }
    );

    if (response.data.success && response.data.data) {
      cache[cacheKey] = {
        translation: response.data.data.translation,
        timestamp: Date.now()
      };

      return response.data.data.translation;
    }

    return text;
  } catch (error) {
    console.error('翻译请求失败:', error);
    return text;
  }
};

export const batchTranslate = async (
  texts: string[],
  from: string = 'zh',
  to: string = 'en'
): Promise<string[]> => {
  const results: string[] = [];
  const uncachedTexts: string[] = [];
  const uncachedIndices: number[] = [];

  texts.forEach((text, index) => {
    if (!text.trim()) {
      results[index] = text;
    } else {
      const cacheKey = `${text}:${from}:${to}`;
      if (cache[cacheKey] && Date.now() - cache[cacheKey].timestamp < CACHE_EXPIRY_MS) {
        results[index] = cache[cacheKey].translation;
      } else {
        uncachedTexts.push(text);
        uncachedIndices.push(index);
      }
    }
  });

  if (uncachedTexts.length > 0) {
    try {
      const response = await axios.post<BatchTranslateResponse>(
        `${API_BASE_URL}/translate/batch`,
        { texts: uncachedTexts, from, to }
      );

      if (response.data.success && response.data.data) {
        response.data.data.forEach((item, index) => {
          const originalIndex = uncachedIndices[index];
          results[originalIndex] = item.translation;
          
          const cacheKey = `${item.text}:${from}:${to}`;
          cache[cacheKey] = {
            translation: item.translation,
            timestamp: Date.now()
          };
        });
      }
    } catch (error) {
      console.error('批量翻译请求失败:', error);
      uncachedIndices.forEach(index => {
        results[index] = texts[index];
      });
    }
  }

  return results;
};

export const clearTranslationCache = (): void => {
  Object.keys(cache).forEach(key => {
    delete cache[key];
  });
};

export const getTranslationCacheSize = (): number => {
  return Object.keys(cache).length;
};